import * as THREE from 'three';
import {
  anyAttention,
  attentionNear,
  characterDetail,
  CrowdActorPool,
  poseActor,
  CrowdRenderer,
  createCrowdLook,
  crowdBodyFor,
  crowdTint,
  loadCrowdBodies,
  MeshyCrowdRenderer,
  TINT_FLOATS,
  pedStyleForId,
  easeYaw,
  hitsSince,
  latestHit,
  playerSpot,
  relativeYaw,
  wrapAngle,
  type AnimAction,
  type AnimInput,
  type HitReaction,
} from '../render/characters';
import { districtAt } from '../world/lowBorough/data';
import { PED_POOL, type Ped } from './pedSim';

/**
 * Instanced pedestrian renderer on the shared character rig (src/render/characters). Each pedestrian
 * gets a seeded look from its id and the district it first appears in (dockers by the basin, clerks
 * and shop girls on The Parade, pensioners in the terraces: build, clothes, hair, face, props) and
 * its own animator: distance-driven walk with counter-swinging arms and planted feet, a run when
 * fleeing, idle habits (a smoke, a look at the watch, arms folded; chatting in groups), heads that
 * turn to a gunshot or to the villain walking past, flinches and staggers when hit, a limp tumble and
 * a proper get-up. All pedestrians share nine InstancedMeshes (fixed draw calls); matrices are written
 * straight into the instance buffers and nothing is allocated per frame (an actor is created when a
 * pedestrian first appears).
 *
 * At MEDIUM and HIGH, once the crowd pack has loaded, every pedestrian is drawn with one of the Meshy bodies
 * instead (crowdBodies.ts: bone-texture instancing, one draw per body), posed by the same animator through the
 * body's own proportions and dressed in its style's colours. LOW, `?classicpeds` and a missing pack keep the
 * procedural crowd.
 */

/** Pedestrians are drawn a touch larger than life so they read from the high top-down camera. */
const VISUAL_SCALE = 1.18;
/** Trim boxes per pedestrian: cuffs, glasses, a cigarette, carried props. */
const TRIMS_PER_PED = 7;
/** Heads turn to the player within this distance (m), to loud events within ATTENTION_R. */
const NOTICE_R = 6.5;
const ATTENTION_R = 24;

const input: AnimInput = { dt: 0, speed: 0, running: false, turnRate: 0, action: 'none', actionProgress: 0, heading: 0 };
const hit: HitReaction = { t: 0, dir: 0, strength: 0 };

export class PedView {
  readonly group = new THREE.Group();
  private readonly crowd: CrowdRenderer;
  private readonly actors = new CrowdActorPool();
  private meshy: MeshyCrowdRenderer | null = null;
  private lastTime = -1;
  private frame = 0;
  private disposed = false;
  private requested = false;

  constructor(scene: THREE.Scene) {
    this.group.name = 'peds';
    this.crowd = new CrowdRenderer(this.group, {
      name: 'ped',
      capacity: PED_POOL,
      hats: ['flatCap', 'trilby', 'headscarf', 'peakedCap', 'bowler'],
      hair: true,
      torso: 'jacket',
      blobsPerCharacter: TRIMS_PER_PED,
    });
    scene.add(this.group);
  }

  /** The crowd pack is fetched the first time the quality is above LOW (LOW never pays for it). */
  private requestBodies(): void {
    if (this.requested) return;
    this.requested = true;
    void loadCrowdBodies().then((bodies) => {
      if (bodies && !this.disposed) this.meshy = new MeshyCrowdRenderer(this.group, bodies, PED_POOL);
    });
  }

  /** Body id per drawn pedestrian (debug hook `crowdBodies`); empty while the procedural crowd draws. */
  bodies(): Record<number, string> {
    const out: Record<number, string> = {};
    const m = this.meshy;
    if (!m) return out;
    for (const a of this.actors.actors) if (a.body >= 0) out[a.key] = m.bodies[a.body]!.id;
    return out;
  }

  /** Draw calls (main pass) and triangles of the crowd last frame, both renderers (debug hook `crowdCost`). */
  cost(): { calls: number; tris: number; shadowCalls: number; shadowTris: number; meshy: number } {
    let calls = 0;
    let tris = 0;
    let shadowCalls = 0;
    let shadowTris = 0;
    this.group.traverse((o) => {
      const m = o as THREE.InstancedMesh;
      if (!m.isInstancedMesh || !m.visible || !m.count) return;
      const g = m.geometry;
      const t = (m.count * (g.index ? g.index.count : g.getAttribute('position').count)) / 3;
      calls++;
      tris += t;
      if (m.castShadow) {
        shadowCalls++;
        shadowTris += t;
      }
    });
    const mc = this.meshy?.cost();
    // A Meshy body: its draw, its shadow stand-in's (empty) main-pass draw and its shadow draw.
    return {
      calls: calls + (mc?.calls ?? 0) * 2,
      tris: Math.round(tris + (mc?.tris ?? 0)),
      shadowCalls: shadowCalls + (mc?.calls ?? 0),
      shadowTris: Math.round(shadowTris + (mc?.shadowTris ?? 0)),
      meshy: mc?.instances ?? 0,
    };
  }

  /**
   * `time` (optional) is the interpolated simulation clock in seconds; animation advances by its
   * delta, so a paused game freezes the crowd. Without it the wall clock is used.
   */
  update(peds: readonly Ped[], alpha: number, time?: number): void {
    const now = time ?? performance.now() / 1000;
    const dt = this.lastTime < 0 ? 0 : Math.min(Math.max(now - this.lastTime, 0), 0.1);
    this.lastTime = now;
    const actors = this.actors;
    const crowd = this.crowd;
    this.frame++;
    const detail = characterDetail();
    crowd.setDetail(detail);
    if (detail !== 'low') this.requestBodies();
    const meshy = detail !== 'low' ? this.meshy : null;
    actors.begin();
    crowd.begin();
    meshy?.begin();
    const n = Math.min(peds.length, PED_POOL);
    const attention = anyAttention(now);
    const hits = hitsSince(now - 1.2);
    const player = playerSpot.valid ? playerSpot : null;
    for (let i = 0; i < n; i++) {
      const p = peds[i]!;
      const px = p.prevX + (p.x - p.prevX) * alpha;
      const pz = p.prevZ + (p.z - p.prevZ) * alpha;
      let a = actors.get(p.id);
      if (!a) {
        const look = createCrowdLook(pedStyleForId(p.id, districtAt({ x: px, z: pz })));
        // Stride maths at the common scale so retired actors can be reused across heights.
        a = actors.create(p.id, look, VISUAL_SCALE, px, pz, p.yaw);
      }
      const down = p.state === 'tumble' || p.state === 'down';
      let action: AnimAction = 'none';
      if (down) {
        action = 'knockdown';
        // Latch the fall direction: the lying pose falls backwards, so a forward fall is drawn turned round.
        if (a.fallFlip === 0) a.fallFlip = p.leanTarget > 0 ? 1 : -1;
      } else if (p.state !== 'getup') {
        a.fallFlip = 0;
        if (p.state === 'cower') action = 'cower';
      }
      const moving = p.state === 'walk' || p.state === 'flee' || p.state === 'cross';
      const turn = dt > 0 ? wrapAngle(p.yaw - a.lastYaw) / dt : 0;
      a.lastYaw = p.yaw;
      a.lastX = px;
      a.lastZ = pz;
      input.dt = dt;
      input.speed = moving ? p.speed : 0;
      input.running = p.state === 'flee';
      input.turnRate = down ? 0 : Math.max(-6, Math.min(6, turn));
      input.action = action;
      input.heading = p.yaw;
      input.social = p.state === 'idle' && p.site >= 0 ? 1 : 0;
      // Heads turn: to a shot or a crash nearby, else to the player passing close (not while running off).
      input.lookWeight = 0;
      if (!down && p.state !== 'flee' && p.state !== 'cower') {
        const at = attention ? attentionNear(px, pz, ATTENTION_R, now) : null;
        if (at) {
          input.lookYaw = relativeYaw(px, pz, p.yaw, at.x, at.z);
          input.lookWeight = 1;
        } else if (player) {
          const dx = player.x - px;
          const dz = player.z - pz;
          if (dx * dx + dz * dz < NOTICE_R * NOTICE_R) {
            input.lookYaw = relativeYaw(px, pz, p.yaw, player.x, player.z);
            input.lookWeight = (player.armed ? 1 : 0.75) * (moving ? 0.7 : 1);
          }
        }
      }
      // Flinch / stagger when a blow or a bullet lands (the sim decides what happens next).
      input.hit = null;
      let reacting = false;
      a.hitT = 99;
      if (hits && !down) {
        const h = latestHit('ped', p.id, now - 1.2);
        if (h) {
          // The actor keeps the clock too: the Meshy crowd reads its hit-flash off it (crowdBodies).
          a.hitT = now - h.time;
          a.hitStrength = h.strength;
          hit.t = now - h.time;
          hit.dir = relativeYaw(px, pz, p.yaw, h.fromX, h.fromZ);
          hit.strength = h.strength;
          input.hit = hit;
          reacting = hit.t < 1;
        }
      }
      const yaw = easeYaw(a, a.fallFlip > 0 ? p.yaw + Math.PI : p.yaw, 14, dt);
      // Pose LOD: walkers re-animate every 3rd frame, people standing about every 6th (slow idle
      // motion; every 3rd while chatting or looking round); in between the last pose slides along
      // with the root. A hit reaction animates every frame.
      const every = moving || down ? 3 : input.social || input.lookWeight ? 3 : 6;
      // Meshy body: chosen once (from the style); the pose goes through the body's skeleton. A change of
      // skeleton (pack loaded, quality switched) forces a full pose this frame.
      if (a.body === -2 && this.meshy) {
        const id = crowdBodyFor(a.look.style);
        a.body = this.meshy.indexOf(id);
        a.tint = a.body >= 0 ? crowdTint(a.look.style, id, a.tint ?? new Float32Array(TINT_FLOATS)) : null;
      }
      const skel = meshy && a.body >= 0 ? meshy.bodies[a.body]!.skeleton : null;
      if (a.skel !== skel) {
        a.skel = skel;
        a.posed = false;
      }
      poseActor(a, input, reacting || (a.key + this.frame) % every === 0, px, p.y, pz, yaw, VISUAL_SCALE * a.look.style.height * p.fade);
      if (skel) meshy!.addActor(a, a.body, a.tint!, px, p.y, pz);
      else crowd.addActor(a, px, p.y, pz);
    }
    actors.end();
    crowd.end();
    meshy?.end();
  }

  dispose(): void {
    this.disposed = true;
    this.group.removeFromParent();
    this.crowd.dispose();
    this.meshy?.dispose();
  }
}

/**
 * Compose the matrix of one body part. The part's pivot sits at local (cx, cy, cz) relative to the
 * ped's feet; `swing` rotates the part about its own X axis and `lean` pitches the whole body about
 * the feet. Scale is applied in the part's own axes (columns of Ry(yaw) * Rx(lean + swing)).
 */
export function writePart(
  arr: Float32Array,
  index: number,
  px: number,
  py: number,
  pz: number,
  sinY: number,
  cosY: number,
  lean: number,
  swing: number,
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
): void {
  const phi = lean + swing;
  const cosL = Math.cos(lean);
  const sinL = Math.sin(lean);
  const cosP = Math.cos(phi);
  const sinP = Math.sin(phi);
  const ly = cy * cosL - cz * sinL;
  const lz = cy * sinL + cz * cosL;
  const wx = cx * cosY + lz * sinY;
  const wz = -cx * sinY + lz * cosY;
  const o = index * 16;
  arr[o] = cosY * sx;
  arr[o + 1] = 0;
  arr[o + 2] = -sinY * sx;
  arr[o + 3] = 0;
  arr[o + 4] = sinP * sinY * sy;
  arr[o + 5] = cosP * sy;
  arr[o + 6] = sinP * cosY * sy;
  arr[o + 7] = 0;
  arr[o + 8] = cosP * sinY * sz;
  arr[o + 9] = -sinP * sz;
  arr[o + 10] = cosP * cosY * sz;
  arr[o + 11] = 0;
  arr[o + 12] = px + wx;
  arr[o + 13] = py + ly;
  arr[o + 14] = pz + wz;
  arr[o + 15] = 1;
}
